import { useState, useEffect, useRef, useCallback } from "react";

export const EXIT_INTENT_CONFIG = {
  sessionKey: "exit_intent_lead_shown",
  dismissCookieName: "exit_intent_lead_dismissed",
  dismissCookieDays: 3,
  pageReadyMs: 15000,
  mobileBreakpoint: 768,
  mobileDelayMs: 60000,
  desktopTopThreshold: 5,
  transitionMs: 380,
};

function isMobile() {
  return typeof window !== "undefined" && window.innerWidth < EXIT_INTENT_CONFIG.mobileBreakpoint;
}

function wasShownInSession() {
  try {
    return sessionStorage.getItem(EXIT_INTENT_CONFIG.sessionKey) === "1";
  } catch {
    return false;
  }
}

function markShownInSession() {
  try {
    sessionStorage.setItem(EXIT_INTENT_CONFIG.sessionKey, "1");
  } catch {
    /* ignore */
  }
}

function clearShownInSession() {
  try {
    sessionStorage.removeItem(EXIT_INTENT_CONFIG.sessionKey);
  } catch {
    /* ignore */
  }
}

function wasDismissedByCookie() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((item) => {
    return item.trim().indexOf(`${EXIT_INTENT_CONFIG.dismissCookieName}=`) === 0;
  });
}

function markDismissedByCookie() {
  const maxAge = EXIT_INTENT_CONFIG.dismissCookieDays * 24 * 60 * 60;
  document.cookie = `${EXIT_INTENT_CONFIG.dismissCookieName}=1; max-age=${maxAge}; path=/; SameSite=Lax`;
}

export function useExitIntentLead({ isModalOpen = false, isCollapsing = false } = {}) {
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [viewportMode, setViewportMode] = useState(() =>
    typeof window !== "undefined" ? isMobile() : false
  );
  const isClosingRef = useRef(false);
  const lastFocusRef = useRef(null);
  const closeTimerRef = useRef(null);
  const isModalOpenRef = useRef(isModalOpen);
  const isActiveRef = useRef(isActive);
  const isCaptureEnabledRef = useRef(false);
  const scrollYRef = useRef(0);
  const [isCaptureEnabled, setIsCaptureEnabled] = useState(false);

  isModalOpenRef.current = isModalOpen;
  isActiveRef.current = isActive;

  useEffect(() => {
    const timerId = setTimeout(() => {
      isCaptureEnabledRef.current = true;
      setIsCaptureEnabled(true);
    }, EXIT_INTENT_CONFIG.pageReadyMs);
    return () => clearTimeout(timerId);
  }, []);

  const shouldBlockShow = useCallback(() => {
    if (!isCaptureEnabledRef.current) return true;
    if (wasShownInSession() || wasDismissedByCookie()) return true;
    if (isActiveRef.current || isClosingRef.current) return true;
    if (isModalOpenRef.current) return true;
    return false;
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    if (shouldBlockShow()) return;
    markShownInSession();
    isClosingRef.current = false;
    lastFocusRef.current = document.activeElement;
    setIsMinimized(false);
    setIsActive(true);
    setIsOpen(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isModalOpenRef.current) {
          clearShownInSession();
          setIsActive(false);
          setIsOpen(false);
          setIsMinimized(false);
          return;
        }
        setIsOpen(true);
      });
    });
  }, [shouldBlockShow]);

  const minimize = useCallback(() => {
    if (!isActiveRef.current || isClosingRef.current) return;
    setIsOpen(false);
    setIsMinimized(true);
  }, []);

  const expand = useCallback(() => {
    if (!isActiveRef.current || isClosingRef.current) return;
    setIsMinimized(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsOpen(true));
    });
  }, []);

  const close = useCallback(
    (rememberDismiss = false) => {
      if (!isActiveRef.current || isClosingRef.current) return;
      if (rememberDismiss) markDismissedByCookie();
      isClosingRef.current = true;
      setIsOpen(false);
      setIsMinimized(false);
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        setIsActive(false);
        isClosingRef.current = false;
        if (lastFocusRef.current && typeof lastFocusRef.current.focus === "function") {
          try {
            lastFocusRef.current.focus();
          } catch {
            /* ignore */
          }
        }
        lastFocusRef.current = null;
      }, EXIT_INTENT_CONFIG.transitionMs);
    },
    [clearCloseTimer]
  );

  const abortDueToSiteModal = useCallback(() => {
    if (!isActiveRef.current) return;
    clearCloseTimer();
    isClosingRef.current = false;
    clearShownInSession();
    setIsOpen(false);
    setIsMinimized(false);
    setIsActive(false);
    lastFocusRef.current = null;
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!isModalOpen) return;
    abortDueToSiteModal();
  }, [isModalOpen, abortDueToSiteModal]);

  useEffect(() => {
    if (!isCaptureEnabled || isModalOpen || shouldBlockShow()) return undefined;

    if (isMobile()) {
      const timerId = setTimeout(() => {
        if (!isMobile() || shouldBlockShow()) return;
        show();
      }, EXIT_INTENT_CONFIG.mobileDelayMs);
      return () => clearTimeout(timerId);
    }

    const onMouseOut = (e) => {
      if (isMobile() || shouldBlockShow()) return;
      if (!e.relatedTarget && e.clientY <= EXIT_INTENT_CONFIG.desktopTopThreshold) {
        show();
      }
    };

    document.documentElement.addEventListener("mouseout", onMouseOut);
    return () => document.documentElement.removeEventListener("mouseout", onMouseOut);
  }, [isCaptureEnabled, isModalOpen, isActive, viewportMode, shouldBlockShow, show]);

  useEffect(() => {
    const onResize = () => {
      setViewportMode(isMobile());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const lockScroll = isActive && (isOpen || isCollapsing) && !isMinimized;
    if (!lockScroll) return undefined;

    scrollYRef.current = window.scrollY;
    document.body.classList.add("exit-intent-lead-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.classList.remove("exit-intent-lead-open");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isActive, isOpen, isMinimized, isCollapsing]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape" || !isActiveRef.current || isCollapsing) return;
      if (isModalOpenRef.current) return;
      if (isMinimized) {
        close(true);
        return;
      }
      if (isOpen) {
        close(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isMinimized, isCollapsing, close]);

  return { isActive, isOpen, isMinimized, show, close, minimize, expand };
}
